import { useState } from 'react';

import { fmt, fmtDate, toNumber, dateToInputValue } from '@dcc-erp/shared';
import type { CustomerStatement, StatementMode } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { downloadFile } from '../../lib/api';
import { useCustomerStatement } from '../../lib/queries';

// On-screen customer statement of account (ใบแจ้งยอดบัญชีลูกหนี้) — Phase 3b
// (AR-11). Owns the mode toggle + date controls + the local query, fetching via
// useCustomerStatement so the route just mounts <CustomerStatementView/>. Two
// modes: activity (balance-forward running ledger, from→to) and open-item
// (currently-open invoices aged as-of a date). The "ดาวน์โหลด PDF" button is a
// disabled placeholder — the .pdf endpoint lands in the next task. Plain
// prototype.css classes only (mirrors AgingReport / InvoiceDetail).

export interface CustomerStatementViewProps {
  companyId: string | undefined;
  customerId: string;
}

const KIND_LABEL: Record<string, string> = {
  invoice: 'ใบแจ้งหนี้',
  receipt: 'รับชำระ',
  creditNote: 'ใบลดหนี้',
  void: 'ยกเลิกใบแจ้งหนี้',
};

const BUCKET_LABEL: Record<string, string> = {
  notDue: 'ยังไม่ครบกำหนด',
  d1_30: '1-30 วัน',
  d31_60: '31-60 วัน',
  d61_90: '61-90 วัน',
  over90: '90+ วัน',
};

export function CustomerStatementView({ companyId, customerId }: CustomerStatementViewProps) {
  const today = new Date();
  const [mode, setMode] = useState<StatementMode>('activity');
  // Activity mode: from blank (= opening 0, all history) + to today. Open-item:
  // as-of today. Blank = server default.
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>(dateToInputValue(today));
  const [asOf, setAsOf] = useState<string>(dateToInputValue(today));

  const query =
    mode === 'activity'
      ? { mode, ...(from ? { from } : {}), ...(to ? { to } : {}) }
      : { mode, ...(asOf ? { asOf } : {}) };

  const { data, isPending, error } = useCustomerStatement(companyId, customerId, query);

  // Authenticated PDF download — the .pdf endpoint is permission-gated, so a bare
  // <a href> won't carry the bearer token; downloadFile fetches WITH the token
  // and triggers the browser save. The query string mirrors the JSON request.
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const canDownload = !!companyId && !!customerId && !!data;

  async function handleDownloadPdf() {
    if (!companyId || !customerId) return;
    const qs = new URLSearchParams();
    qs.set('mode', mode);
    if (mode === 'activity') {
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
    } else if (asOf) {
      qs.set('asOf', asOf);
    }
    const path = `/companies/${companyId}/ar/customers/${customerId}/statement.pdf?${qs.toString()}`;
    const fallbackName = `statement-${data?.customerCode ?? customerId}.pdf`;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadFile(path, fallbackName);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="tbl-shell">
      {/* Mode toggle + the disabled PDF placeholder */}
      <div
        className="filter-row"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div role="tablist" style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'activity'}
            className={mode === 'activity' ? 'btn btn-primary' : 'btn'}
            onClick={() => setMode('activity')}
          >
            เคลื่อนไหว
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'openItem'}
            className={mode === 'openItem' ? 'btn btn-primary' : 'btn'}
            onClick={() => setMode('openItem')}
          >
            รายการค้างชำระ
          </button>
        </div>
        <button
          type="button"
          className="btn"
          disabled={!canDownload || downloading}
          onClick={() => void handleDownloadPdf()}
          aria-label="ดาวน์โหลด PDF"
        >
          <Icon name="download" size={14} /> {downloading ? 'กำลังดาวน์โหลด…' : 'ดาวน์โหลด PDF'}
        </button>
      </div>

      {downloadError && (
        <div className="neg" style={{ padding: '4px 0' }} role="alert">
          ดาวน์โหลด PDF ไม่สำเร็จ: {downloadError}
        </div>
      )}

      {/* Date controls — activity: from + to; open-item: as-of */}
      <div className="filter-row" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {mode === 'activity' ? (
          <>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="muted">ตั้งแต่</span>
              <input
                className="input w-sm"
                type="date"
                aria-label="ตั้งแต่วันที่"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="muted">ถึง</span>
              <input
                className="input w-sm"
                type="date"
                aria-label="ถึงวันที่"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </>
        ) : (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="muted">ณ วันที่</span>
            <input
              className="input w-sm"
              type="date"
              aria-label="ณ วันที่"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </label>
        )}
      </div>

      {isPending && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      )}
      {error && (
        <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      )}

      {data && (
        <>
          {/* Header block — customer code/name/taxId/terms/credit limit */}
          <div className="card" style={{ marginBottom: 12, padding: 12 }}>
            <div style={{ fontWeight: 600 }}>
              <span className="code">{data.customerCode}</span> {data.customerName}
            </div>
            <div className="muted" style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <span>เลขประจำตัวผู้เสียภาษี: {data.taxId ?? '—'}</span>
              <span>เงื่อนไขชำระ: {data.terms ?? '—'}</span>
              <span>วงเงินเครดิต: {fmt(toNumber(data.creditLimit), 2)}</span>
            </div>
          </div>

          {data.mode === 'activity' ? <ActivityTable data={data} /> : <OpenItemTable data={data} />}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity (balance-forward) table: opening row, every AR-affecting event with a
// running balance, closing row.
function ActivityTable({ data }: { data: CustomerStatement }) {
  const lines = data.activityLines ?? [];
  return (
    <div className="tbl-wrap" style={{ maxHeight: 600 }}>
      <table className="tbl">
        <thead>
          <tr>
            <th>วันที่</th>
            <th>รายการ</th>
            <th>อ้างอิง</th>
            <th className="num">เดบิต</th>
            <th className="num">เครดิต</th>
            <th className="num">คงเหลือ</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={5}>ยอดยกมา</td>
            <td className="num tnum">{fmt(toNumber(data.opening), 2)}</td>
          </tr>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                ไม่มีรายการเคลื่อนไหว
              </td>
            </tr>
          ) : (
            lines.map((l, i) => (
              <tr key={`${l.ref}-${i}`}>
                <td>{fmtDate(l.date)}</td>
                <td>{KIND_LABEL[l.kind] ?? l.kind}</td>
                <td>
                  <span className="code">{l.ref}</span>
                </td>
                <td className="num tnum">{toNumber(l.debit) ? fmt(toNumber(l.debit), 2) : '—'}</td>
                <td className="num tnum">
                  {toNumber(l.credit) ? fmt(toNumber(l.credit), 2) : '—'}
                </td>
                <td className="num tnum">{fmt(toNumber(l.balance), 2)}</td>
              </tr>
            ))
          )}
          <tr className="total">
            <td colSpan={5}>ยอดคงเหลือยกไป</td>
            <td className="num tnum">{fmt(toNumber(data.closing), 2)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Open-item table: outstanding (>0) non-VOID invoices aged vs the as-of date,
// plus the 5-bucket aging summary + grand total.
function OpenItemTable({ data }: { data: CustomerStatement }) {
  const lines = data.openItemLines ?? [];
  const buckets = data.buckets;
  return (
    <>
      <div className="tbl-wrap" style={{ maxHeight: 480 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>ลงวันที่</th>
              <th>ครบกำหนด</th>
              <th className="num">จำนวน</th>
              <th className="num">ค้างชำระ</th>
              <th className="num">อายุหนี้ (วัน)</th>
              <th>ช่วงอายุ</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีรายการค้างชำระ
                </td>
              </tr>
            ) : (
              lines.map((l) => (
                <tr key={l.voucherNo}>
                  <td>
                    <span className="code">{l.voucherNo}</span>
                  </td>
                  <td>{fmtDate(l.invoiceDate)}</td>
                  <td>{fmtDate(l.dueDate)}</td>
                  <td className="num tnum">{fmt(toNumber(l.total), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(l.outstanding), 2)}</td>
                  <td className="num tnum">{l.agingDays}</td>
                  <td>{BUCKET_LABEL[l.bucket] ?? l.bucket}</td>
                </tr>
              ))
            )}
            <tr className="total">
              <td colSpan={4}>รวมค้างชำระ</td>
              <td className="num tnum">{fmt(toNumber(data.total), 2)}</td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Aging-bucket summary */}
      {buckets && (
        <div className="tbl-wrap" style={{ marginTop: 12 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{BUCKET_LABEL.notDue}</th>
                <th>{BUCKET_LABEL.d1_30}</th>
                <th>{BUCKET_LABEL.d31_60}</th>
                <th>{BUCKET_LABEL.d61_90}</th>
                <th>{BUCKET_LABEL.over90}</th>
                <th className="num">รวม</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="num tnum">{fmt(toNumber(buckets.notDue), 2)}</td>
                <td className="num tnum">{fmt(toNumber(buckets.d1_30), 2)}</td>
                <td className="num tnum">{fmt(toNumber(buckets.d31_60), 2)}</td>
                <td className="num tnum">{fmt(toNumber(buckets.d61_90), 2)}</td>
                <td className="num tnum">{fmt(toNumber(buckets.over90), 2)}</td>
                <td className="num tnum">{fmt(toNumber(data.total), 2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
